import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Alert,
  CircularProgress,
  SelectChangeEvent
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import CSVFileUploader from '../../../components/csv-import/CSVFileUploader';
import { fuelCSVImportService } from '../../../services/fuelCSVImportService';
import { getSuppliers } from '../../../services/supplierService';
import { Supplier } from '../../../types/supplier';

/**
 * Page principale d'importation CSV pour les consommations de carburant
 */
const FuelImportPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const { currentUser, currentOrganization } = useAuth();
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Étapes de l'importation
  const steps = [
    'Sélection du fichier',
    'Configuration du mapping',
    'Importation des données',
    'Résultats'
  ];
  
  // Charger les fournisseurs au chargement de la page
  useEffect(() => {
    const loadSuppliers = async () => {
      if (!currentOrganization?.id) return;
      
      try {
        const suppliersData = await getSuppliers(currentOrganization.id);
        // Utiliser tous les fournisseurs, pas seulement ceux avec une configuration CSV
        setSuppliers(suppliersData);
      } catch (error) {
        console.error('Erreur lors du chargement des fournisseurs:', error);
        setError('Impossible de charger la liste des fournisseurs.');
      }
    };
    
    loadSuppliers();
  }, [currentOrganization?.id]);
  
  // Gérer la sélection d'un fichier
  const handleFileSelected = (file: File) => {
    setSelectedFile(file);
    setError(null);
  };
  
  // Gérer la sélection d'un fournisseur
  const handleSupplierChange = (event: SelectChangeEvent<string>) => {
    setSelectedSupplierId(event.target.value);
    setError(null);
  };
  
  // Gérer le clic sur le bouton "Continuer"
  const handleContinue = async () => {
    if (!selectedFile) {
      setError('Veuillez sélectionner un fichier CSV.');
      return;
    }
    
    // Vérifier que l'utilisateur est connecté et a une organisation
    if (!currentOrganization || !currentUser) {
      setError('Vous devez être connecté pour effectuer cette action.');
      return;
    }
    
    // Vérifier que l'organisation et l'utilisateur ont des IDs valides
    if (!currentOrganization.id || !currentUser.id) {
      setError('Informations utilisateur ou organisation incomplètes.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Analyser le fichier CSV
      const { headers, data } = await fuelCSVImportService.parseCSV(selectedFile, null);
      
      // Stocker les données dans le localStorage pour les récupérer dans la page suivante
      localStorage.setItem('fuelImport_headers', JSON.stringify(headers));
      localStorage.setItem('fuelImport_data', JSON.stringify(data));
      localStorage.setItem('fuelImport_fileName', selectedFile.name);
      localStorage.setItem('fuelImport_fileSize', String(selectedFile.size));
      
      if (selectedSupplierId) {
        localStorage.setItem('fuelImport_supplierId', selectedSupplierId);
      } else {
        localStorage.removeItem('fuelImport_supplierId');
      }
      
      // Rediriger vers la page de configuration du mapping
      console.log('Navigation vers la page de mapping...');
      navigate('/admin/fuel/import/mapping');
      console.log('Navigation terminée');
    } catch (error) {
      console.error('Erreur lors de l\'analyse du fichier CSV:', error);
      setError('Impossible d\'analyser le fichier CSV. Vérifiez que le format est correct.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Importation de consommations de carburant
        </Typography>
        
        <Stepper activeStep={0} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sélection du fichier CSV
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Sélectionnez un fichier CSV contenant les transactions de carburant à importer.
            Le fichier doit contenir au minimum les colonnes suivantes : date, plaque d'immatriculation,
            type de produit, quantité et montant.
          </Typography>
          
          <CSVFileUploader
            onFileSelected={handleFileSelected}
            loading={loading}
            error={error || undefined}
          />
          
          <Divider sx={{ my: 3 }} />
          
          <Typography variant="h6" gutterBottom>
            Sélection du fournisseur
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Sélectionnez le fournisseur de carburant correspondant au fichier CSV.
            Si vous importez un fichier d'un nouveau fournisseur, laissez ce champ vide.
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="supplier-select-label">Fournisseur</InputLabel>
            <Select
              labelId="supplier-select-label"
              value={selectedSupplierId}
              onChange={handleSupplierChange}
              label="Fournisseur"
              disabled={loading}
            >
              <MenuItem value="">
                <em>Nouveau fournisseur</em>
              </MenuItem>
              {suppliers.map((supplier) => (
                <MenuItem key={supplier.id} value={supplier.id || ''}>
                  {supplier.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {suppliers.length === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Aucun fournisseur n'a été trouvé. Vous pourrez créer une nouvelle configuration lors de l'importation.
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleContinue}
              disabled={!selectedFile || loading}
              startIcon={loading ? <CircularProgress size={20} /> : undefined}
            >
              {loading ? 'Chargement...' : 'Continuer'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default FuelImportPage;